// =====================================================
// OPTION 2: PREMIUM RECOMMENDATION CARD
// A beautifully styled card for Academy Guidance recommendations
// Save as: frontend/src/components/RecommendationCard.js
// =====================================================

import React from 'react';
import './RecommendationCard.css';

/**
 * Parse the AI recommendation text into structured sections
 */
const parseRecommendation = (text) => {
  const sections = {
    summary: null,
    programs: [],
    learningPath: [],
    considerations: []
  };
  
  if (!text) return sections;
  
  // Extract Assessment Summary
  const summaryMatch = text.match(/📊\s*\*?\*?ASSESSMENT SUMMARY\*?\*?([\s\S]*?)(?=🎯|---|$)/i);
  if (summaryMatch) {
    const summaryText = summaryMatch[1].trim();
    const summaryItems = {};
    
    const patterns = [
      { key: 'currentSituation', pattern: /\*?\*?Current Situation\*?\*?:\s*([^\n•]+)/i },
      { key: 'strengths', pattern: /\*?\*?Key Strengths\*?\*?:\s*([^\n•]+)/i },
      { key: 'developmentAreas', pattern: /\*?\*?Development Areas\*?\*?:\s*([^\n•]+)/i },
      { key: 'learningStyle', pattern: /\*?\*?Learning Style\*?\*?:\s*([^\n•]+)/i },
      { key: 'careerGoal', pattern: /\*?\*?Career Goal\*?\*?:\s*([^\n•]+)/i },
      { key: 'constraints', pattern: /\*?\*?Constraints\*?\*?:\s*([^\n•]+)/i }
    ];
    
    patterns.forEach(({ key, pattern }) => {
      const match = summaryText.match(pattern);
      if (match) summaryItems[key] = match[1].trim();
    });
    
    sections.summary = summaryItems;
  }
  
  // Extract Programs
  const programsMatch = text.match(/🎯\s*\*?\*?RECOMMENDED PROGRAMS\*?\*?([\s\S]*?)(?=📋|---|$)/i);
  if (programsMatch) {
    const programsText = programsMatch[1];
    
    // Primary recommendation
    const primaryMatch = programsText.match(/\*?\*?Primary Recommendation:\s*([^\n*]+)\*?\*?([\s\S]*?)(?=\*?\*?Alternative|\*?\*?Future|$)/i);
    if (primaryMatch) {
      sections.programs.push({
        type: 'primary',
        name: primaryMatch[1].trim(),
        details: extractProgramDetails(primaryMatch[2])
      });
    }
    
    // Alternative
    const altMatch = programsText.match(/\*?\*?Alternative Option:\s*([^\n*]+)\*?\*?([\s\S]*?)(?=\*?\*?Future|$)/i);
    if (altMatch) {
      sections.programs.push({
        type: 'alternative',
        name: altMatch[1].trim(),
        details: extractProgramDetails(altMatch[2])
      });
    }
    
    // Future
    const futureMatch = programsText.match(/\*?\*?Future Development:\s*([^\n*]+)\*?\*?([\s\S]*?)$/i);
    if (futureMatch) {
      sections.programs.push({
        type: 'future',
        name: futureMatch[1].trim(),
        details: extractProgramDetails(futureMatch[2])
      });
    }
  }
  
  // Extract Learning Path
  const pathMatch = text.match(/📋\s*\*?\*?RECOMMENDED LEARNING PATH\*?\*?([\s\S]*?)(?=💡|---|$)/i);
  if (pathMatch) {
    const pathText = pathMatch[1];
    const steps = pathText.match(/\d+\.\s*\*?\*?([^:]+):\*?\*?\s*([^\n]+)/g);
    if (steps) {
      sections.learningPath = steps.map(step => {
        const match = step.match(/\d+\.\s*\*?\*?([^:]+):\*?\*?\s*(.+)/);
        return match ? { action: match[1].trim(), description: match[2].trim() } : null;
      }).filter(Boolean);
    }
  }
  
  // Extract Considerations
  const considerMatch = text.match(/💡\s*\*?\*?ADDITIONAL CONSIDERATIONS\*?\*?([\s\S]*?)$/i);
  if (considerMatch) {
    const considerText = considerMatch[1];
    const items = considerText.match(/[•\-]\s*([^\n]+)/g);
    if (items) {
      sections.considerations = items.map(item => item.replace(/^[•\-]\s*/, '').trim());
    }
  }
  
  return sections;
};

const extractProgramDetails = (text) => {
  const details = {};
  
  const whyMatch = text.match(/Why (?:this fits|this might work|this could work)[^:]*:\s*([^\n•]+)/i);
  if (whyMatch) details.why = whyMatch[1].trim();
  
  const formatMatch = text.match(/Format:\s*([^\n•]+)/i);
  if (formatMatch) details.format = formatMatch[1].trim();
  
  const durationMatch = text.match(/Duration:\s*([^\n•]+)/i);
  if (durationMatch) details.duration = durationMatch[1].trim();
  
  const bestIfMatch = text.match(/Best if:\s*([^\n•]+)/i);
  if (bestIfMatch) details.bestIf = bestIfMatch[1].trim();
  
  const considerMatch = text.match(/Consider (?:this )?if:\s*([^\n•]+)/i);
  if (considerMatch) details.considerIf = considerMatch[1].trim();
  
  return details;
};

/**
 * Check if message content is a recommendation
 */
export const isRecommendationMessage = (content) => {
  if (!content) return false;
  return (
    content.includes('📊') && 
    content.includes('ASSESSMENT SUMMARY') &&
    content.includes('🎯') &&
    content.includes('RECOMMENDED')
  );
};

/**
 * RecommendationCard Component
 */
const RecommendationCard = ({ content }) => {
  const sections = parseRecommendation(content);
  
  return (
    <div className="recommendation-card">
      {/* Header */}
      <div className="rec-header">
        <span className="rec-icon">🎓</span>
        <h3>Your Program Recommendations</h3>
      </div>
      
      {/* Assessment Summary */}
      {sections.summary && Object.keys(sections.summary).length > 0 && (
        <div className="rec-section rec-summary">
          <div className="rec-section-header">
            <span className="rec-section-icon">📊</span>
            <h4>Assessment Summary</h4>
          </div>
          <div className="rec-summary-grid">
            {sections.summary.currentSituation && (
              <div className="summary-item">
                <span className="summary-label">Current Situation</span>
                <span className="summary-value">{sections.summary.currentSituation}</span>
              </div>
            )}
            {sections.summary.careerGoal && (
              <div className="summary-item">
                <span className="summary-label">Career Goal</span>
                <span className="summary-value">{sections.summary.careerGoal}</span>
              </div>
            )}
            {sections.summary.strengths && (
              <div className="summary-item">
                <span className="summary-label">Key Strengths</span>
                <span className="summary-value">{sections.summary.strengths}</span>
              </div>
            )}
            {sections.summary.developmentAreas && (
              <div className="summary-item">
                <span className="summary-label">Development Areas</span>
                <span className="summary-value">{sections.summary.developmentAreas}</span>
              </div>
            )}
            {sections.summary.learningStyle && (
              <div className="summary-item">
                <span className="summary-label">Learning Style</span>
                <span className="summary-value">{sections.summary.learningStyle}</span>
              </div>
            )}
            {sections.summary.constraints && (
              <div className="summary-item">
                <span className="summary-label">Constraints</span>
                <span className="summary-value">{sections.summary.constraints}</span>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Recommended Programs */}
      {sections.programs.length > 0 && (
        <div className="rec-section rec-programs">
          <div className="rec-section-header">
            <span className="rec-section-icon">🎯</span>
            <h4>Recommended Programs</h4>
          </div>
          <div className="rec-programs-list">
            {sections.programs.map((program, index) => (
              <div key={index} className={`program-card program-${program.type}`}>
                <div className="program-badge">
                  {program.type === 'primary' && '⭐ Primary'}
                  {program.type === 'alternative' && '🔄 Alternative'}
                  {program.type === 'future' && '🚀 Future'}
                </div>
                <h5 className="program-name">{program.name}</h5>
                {program.details.why && (
                  <p className="program-why">{program.details.why}</p>
                )}
                <div className="program-meta">
                  {program.details.format && (
                    <span className="meta-item">
                      <span className="meta-icon">📅</span>
                      {program.details.format}
                    </span>
                  )}
                  {program.details.duration && (
                    <span className="meta-item">
                      <span className="meta-icon">⏱️</span>
                      {program.details.duration}
                    </span>
                  )}
                </div>
                {(program.details.bestIf || program.details.considerIf) && (
                  <p className="program-condition">
                    <strong>Best if:</strong> {program.details.bestIf || program.details.considerIf}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Learning Path */}
      {sections.learningPath.length > 0 && (
        <div className="rec-section rec-path">
          <div className="rec-section-header">
            <span className="rec-section-icon">📋</span>
            <h4>Recommended Learning Path</h4>
          </div>
          <div className="path-timeline">
            {sections.learningPath.map((step, index) => (
              <div key={index} className="path-step">
                <div className="path-number">{index + 1}</div>
                <div className="path-content">
                  <span className="path-action">{step.action}</span>
                  <span className="path-description">{step.description}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Considerations */}
      {sections.considerations.length > 0 && (
        <div className="rec-section rec-considerations">
          <div className="rec-section-header">
            <span className="rec-section-icon">💡</span>
            <h4>Additional Considerations</h4>
          </div>
          <ul className="considerations-list">
            {sections.considerations.map((item, index) => (
              <li key={index}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Footer */}
      <div className="rec-footer">
        <p>Have questions about these recommendations? Feel free to ask!</p>
      </div>
    </div>
  );
};

export default RecommendationCard;